import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VehicleCard from '../components/VehicleCard';
import { Box, Container, Typography, useMediaQuery } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";


const ServicesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Handle scroll for parallax effects
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };
  
  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };
  
  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };
  
  // Common card style to ensure consistency
  const commonCardStyle = {
    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
    borderRadius: "12px",
    overflow: "hidden",
    transition: "all 0.3s ease",
  };


  return (
    <div style={{ overflow: "hidden" }}>
      {/* Background pattern */}
      <div style={{ 
        position: "fixed", 
        width: "100%", 
        height: "100vh", 
        zIndex: -1,
        backgroundImage: "url('/images/world-map-background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: 0.05
      }} />
      
      <Header />
      
      {/* Page Title */}
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{ 
              mt: { xs: 4, sm: 5, md: 6 }, 
              mb: { xs: 1.5, sm: 2 }, 
              textAlign: "center",
              color: "#db352a",
              fontWeight: "bold",
              fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
              position: "relative",
              px: { xs: 2, sm: 0 },
              "&::after": {
                content: '""',
                position: "absolute",
                width: { xs: "60px", sm: "80px" },
                height: { xs: "3px", sm: "4px" },
                backgroundColor: "#db352a",
                bottom: { xs: "-10px", sm: "-15px" },
                left: "50%",
                transform: "translateX(-50%)"
              }
            }}
          >
            Our Services
          </Typography>
        </motion.div>

        {/* Pickup Service Notice - SUBTLE NEUTRAL DESIGN */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <Box
            sx={{
              mt: { xs: 4, sm: 5, md: 6 },
              mb: { xs: 3, sm: 4 },
              mx: { xs: 2, sm: 0 },
              background: "linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)",
              borderRadius: { xs: "12px", sm: "16px" },
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              position: "relative"
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1, p: { xs: 2.5, sm: 3, md: 4 } }}>
              {/* Header */}
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: { xs: 2, sm: 2.5, md: 3 },
                flexWrap: "wrap",
                gap: { xs: 1, sm: 1.5 },
                pb: { xs: 2, sm: 2.5 },
                borderBottom: "2px solid #db352a"
              }}>
                <LocalTaxiIcon sx={{ 
                  color: "#db352a", 
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                  flexShrink: 0
                }} />
                <Typography
                  variant="h4"
                  sx={{
                    color: "#2c3e50",
                    fontWeight: "bold",
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
                    lineHeight: 1.3
                  }}
                >
                  Complimentary Pickup Service
                </Typography>
              </Box>
              
              {/* Time Cards */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: 2, sm: 2.5, md: 3 },
                  mt: { xs: 2, sm: 2.5, md: 3 }
                }}
              >
                {/* Free Pickup Card */}
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "#ffffff",
                    borderRadius: { xs: "10px", sm: "12px" },
                    p: { xs: 2, sm: 2.5, md: 3 },
                    display: "flex",
                    alignItems: "start",
                    gap: { xs: 1.5, sm: 2 },
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    border: "2px solid #4caf50",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(76,175,80,0.15)",
                      transform: "translateY(-2px)"
                    },
                    "&:active": {
                      transform: "scale(0.98)"
                    }
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#e8f5e9",
                      borderRadius: "50%",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ 
                      color: "#4caf50", 
                      fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }
                    }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#2c3e50",
                        fontWeight: "bold",
                        mb: { xs: 0.5, sm: 0.75 },
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                        lineHeight: 1.3
                      }}
                    >
                      FREE Pickup
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#4caf50",
                        color: "white",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "6px",
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        fontWeight: "600",
                        mb: { xs: 1, sm: 1.25 }
                      }}
                    >
                      6:00 AM - 8:00 PM
                    </Box>
                    <Typography
                      sx={{
                        color: "#546e7a",
                        fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                        lineHeight: 1.6
                      }}
                    >
                      Complimentary pickup from Aurangabad Railway Station or Bus Stand
                    </Typography>
                  </Box>
                </Box>

                {/* Chargeable Pickup Card */}
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "#ffffff",
                    borderRadius: { xs: "10px", sm: "12px" },
                    p: { xs: 2, sm: 2.5, md: 3 },
                    display: "flex",
                    alignItems: "start",
                    gap: { xs: 1.5, sm: 2 },
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    border: "2px solid #ff9800",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(255,152,0,0.15)",
                      transform: "translateY(-2px)"
                    },
                    "&:active": {
                      transform: "scale(0.98)"
                    }
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#fff3e0",
                      borderRadius: "50%",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}
                  >
                    <AccessTimeIcon sx={{ 
                      color: "#ff9800", 
                      fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }
                    }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#2c3e50",
                        fontWeight: "bold",
                        mb: { xs: 0.5, sm: 0.75 },
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                        lineHeight: 1.3
                      }}
                    >
                      Chargeable
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#ff9800",
                        color: "white",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "6px",
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        fontWeight: "600",
                        mb: { xs: 1, sm: 1.25 }
                      }}
                    >
                      8:00 PM - 6:00 AM
                    </Box>
                    <Typography
                      sx={{
                        color: "#546e7a",
                        fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                        lineHeight: 1.6
                      }}
                    >
                      Pickup available with nominal charges during late night/early morning hours
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Info Banner */}
              <Box
                sx={{
                  mt: { xs: 2, sm: 2.5, md: 3 },
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  backgroundColor: "#e3f2fd",
                  borderRadius: { xs: "8px", sm: "10px" },
                  display: "flex",
                  alignItems: "start",
                  gap: { xs: 1, sm: 1.5 },
                  border: "1px solid #90caf9"
                }}
              >
                <InfoIcon sx={{ 
                  color: "#1976d2", 
                  fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.4rem" },
                  flexShrink: 0,
                  mt: 0.2
                }} />
                <Typography
                  sx={{
                    color: "#1565c0",
                    fontSize: { xs: "0.8rem", sm: "0.875rem", md: "0.95rem" },
                    fontWeight: "500",
                    lineHeight: 1.5
                  }}
                >
                  Please mention your arrival time when booking to ensure smooth pickup service
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>


      {/* Hire Cars Section */}
      <Box sx={{ position: "relative", overflow: "hidden", pb: { xs: 6, sm: 8, md: 10 }, pt: { xs: 2, sm: 3, md: 4 } }}>
        {/* Decorative Elements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          style={{
            position: "absolute",
            left: isMobile ? "-100px" : "-70px",
            top: isMobile ? "150px" : "200px",
            zIndex: 0,
            display: isMobile ? "none" : "block"
          }}
        >
          <motion.img
            animate={{ 
              y: [0, 15, 0],
              rotate: [0, 5, 0]
            }}
            transition={{ 
              duration: 5, 
              repeat: Infinity,
              repeatType: "reverse"
            }}
            src="/illustrations/traffic cone.png"
            alt="Traffic cone"
            style={{
              width: isTablet ? "120px" : "150px",
              height: "auto",
              filter: "drop-shadow(0px 5px 10px rgba(0,0,0,0.15))"
            }}
          />
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          style={{
            position: "absolute",
            right: isMobile ? "-200px" : isTablet ? "-100px" : "-50px",
            top: isMobile ? "100px" : "100px",
            zIndex: 0
          }}
        >
          <motion.img
            animate={{ 
              y: [0, -10, 0],
              x: [0, -5, 0]
            }}
            transition={{ 
              duration: 6, 
              repeat: Infinity,
              repeatType: "reverse"
            }}
            src="/illustrations/road going into the distance.png"
            alt="Road"
            style={{
              width: isMobile ? "250px" : isTablet ? "300px" : "400px",
              height: "auto",
              opacity: 0.6,
              filter: "drop-shadow(0px 5px 10px rgba(0,0,0,0.15))"
            }}
          />
        </motion.div>
      
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInLeft}
          >
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center",
                background: "linear-gradient(90deg, rgba(219,53,42,0.1) 0%, rgba(255,255,255,0) 100%)",
                borderRadius: { xs: "30px", sm: "40px", md: "50px" },
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                width: "fit-content",
                my: { xs: 3, sm: 3.5, md: 4 },
                mx: { xs: 2, sm: 0 }
              }}
            >
              <DirectionsCarIcon sx={{ 
                color: "#db352a", 
                mr: { xs: 1, sm: 1.5, md: 2 }, 
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }
              }} />
              <Typography
                variant="h4"
                component="h2"
                sx={{ 
                  fontWeight: "medium",
                  color: "#db352a",
                  fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" }
                }}
              >
                Hire Cars in Budget
              </Typography>
            </Box>
          </motion.div>


          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerChildren}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 3, sm: 4, md: 8 },
                my: { xs: 4, sm: 5, md: 6 },
                justifyContent: "center",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                px: { xs: 2, sm: 0 }
              }}
            >
              <motion.div 
                variants={fadeIn}
                whileHover={{ y: -10 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <VehicleCard
                  image={"/images/SedanCar.png"}
                  name={"Sedan"}
                  description={
                    "Capacity: 4 + Driver, Luggage: 2 Big + 2 Small, Music Player: Yes, AC: Yes"
                  }
                  sx={commonCardStyle}
                />
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                whileHover={{ y: -10 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <VehicleCard
                  image={"/images/InnovaCar.jpg"}
                  name={"Innova"}
                  description={
                    "Capacity: 6 + 1  Driver, Luggage: 5 Big + 2 Small, Music Player: Yes, AC: Yes"
                  }
                  sx={commonCardStyle}
                />
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>


      {/* Tempo Traveler Section */}
      <Box
        sx={{
          width: "100%",
          position: "relative",
          background: "linear-gradient(135deg, #db352a 0%, #ff7366 100%)",
          py: { xs: 6, sm: 7, md: 8 },
          overflow: "hidden"
        }}
      >
        {/* Background pattern */}
        <Box sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "url('/images/world-map-dots.png')",
          backgroundSize: "cover",
          opacity: 0.1,
          zIndex: 0
        }} />
        
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInRight}
          >
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center",
                background: "rgba(255,255,255,0.2)",
                borderRadius: { xs: "30px", sm: "40px", md: "50px" },
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                width: "fit-content",
                my: { xs: 3, sm: 3.5, md: 4 },
                mx: { xs: 2, sm: 0 }
              }}
            >
              <AirportShuttleIcon sx={{ 
                color: "white", 
                mr: { xs: 1, sm: 1.5, md: 2 }, 
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }
              }} />
              <Typography
                variant="h4"
                component="h2"
                sx={{ 
                  fontWeight: "medium",
                  color: "white",
                  fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" }
                }}
              >
                Hire Tempo Traveler
              </Typography>
            </Box>
          </motion.div>


          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerChildren}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 3, sm: 3.5, md: 4 },
                my: { xs: 3, sm: 3.5, md: 4 },
                justifyContent: "center",
                alignItems: "center",
                px: { xs: 2, sm: 0 }
              }}
            >
              {[
                {
                  image: "/images/TempotTaveller.jpg",
                  name: "17 + 1 Seater Tempo Traveler", 
                  description: "Capacity: 17 + 1,  Music Player: Yes, AC: Yes"
                },
                {
                  image: "/images/TempoTraveller2.jpg",
                  name: "26 + 1 Seater Tempo Traveler",
                  description: "Capacity: 26 + 1,  Music Player: Yes, AC: Yes"
                },
                {
                  image: "/images/TempoTraveller3.jpg",
                  name: "20 + 1 Seater Tempo Traveler",
                  description: "Capacity: 20 + 1,  Music Player: Yes, AC: Yes"
                }
              ].map((vehicle, index) => (
                <motion.div 
                  key={index}
                  variants={fadeIn}
                  whileHover={{ y: -10 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <VehicleCard
                    image={vehicle.image}
                    name={vehicle.name}
                    description={vehicle.description}
                    version="white"
                    sx={commonCardStyle}
                  />
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>


      {/* Hire Bus Section */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          py: { xs: 6, sm: 7, md: 8 },
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInLeft}
          >
            <Box 
              sx={{ 
                display: "flex", 
                alignItems: "center",
                background: "linear-gradient(90deg, rgba(219,53,42,0.1) 0%, rgba(255,255,255,0) 100%)",
                borderRadius: { xs: "30px", sm: "40px", md: "50px" },
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                width: "fit-content",
                my: { xs: 3, sm: 3.5, md: 4 },
                mx: { xs: 2, sm: 0 }
              }}
            >
              <DirectionsBusIcon sx={{ 
                color: "#db352a", 
                mr: { xs: 1, sm: 1.5, md: 2 }, 
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" }
              }} />
              <Typography
                variant="h4"
                component="h2"
                sx={{ 
                  fontWeight: "medium",
                  color: "#db352a",
                  fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" }
                }}
              >
                Hire Bus
              </Typography>
            </Box>
          </motion.div>


          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerChildren}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 3, sm: 4, md: 8 },
                mt: { xs: 3, sm: 3.5, md: 4 },
                mb: { xs: 6, sm: 7, md: 8 },
                justifyContent: "center",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
                px: { xs: 2, sm: 0 }
              }}
            >
              <motion.div 
                variants={fadeIn}
                whileHover={{ y: -10 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <VehicleCard
                  image={"/images/Bus1.jpg"}
                  name={"AC sleeper 36 seater"}
                  description={
                    "Capacity: 36 + 1, Driver Music Player: Yes, AC: Yes"
                  }
                  version="red"
                  sx={commonCardStyle}
                />
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                whileHover={{ y: -10 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <VehicleCard
                  image={"/images/Bus2.jpg"}
                  name={"AC 49 + 1 volvo sleeping "}
                  description={
                    "Capacity: 49 + 1, Driver Music Player: Yes, AC: Yes"
                  }
                  version="red"
                  sx={commonCardStyle}
                />
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>
      
      <Footer />
    </div>
  );
};


export default ServicesPage;
